import { useState, useMemo } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
} from '@/components/ui/chart';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export const description = 'Xp earning patterns over time';

const chartConfig = {
    xp: {
        label: 'XP',
        color: 'hsl(var(--primary))',
    },
    previousXp: {
        label: 'Previous Week XP',
        color: 'hsl(var(--primary) / 0.4)',
    },
};

const getDayOfYear = date => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
};

const processStreakData = (streakString, timeRange) => {
    if (!streakString) return [];

    const xpPerDay = streakString.split(',').map(Number);
    const today = new Date();

    let daysToShow;
    switch (timeRange) {
        case '3m':
            daysToShow = 90;
            break;
        case '30d':
            daysToShow = 30;
            break;
        case '7d':
        default:
            daysToShow = 7;
            break;
    }

    const chartData = [];
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - daysToShow + 1);

    for (let i = 0; i < daysToShow; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dayIndex = getDayOfYear(date) - 1;

        const dataPoint = {
            date: date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            }),
            xp: xpPerDay[dayIndex] || 0,
        };

        if (timeRange === '7d') {
            const prevWeekDayIndex = dayIndex - 7;
            dataPoint.previousXp = xpPerDay[prevWeekDayIndex] || 0;
        }

        chartData.push(dataPoint);
    }

    return chartData;
};

export function XpTimelineChart({ data: streakString }) {
    const [timeRange, setTimeRange] = useState('7d');

    const chartData = useMemo(
        () => processStreakData(streakString, timeRange),
        [streakString, timeRange]
    );

    return (
        <Card className="w-full bg-transparent border-none shadow-none">
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>XP Timeline</CardTitle>
                        <CardDescription>Your XP earning patterns over time</CardDescription>
                    </div>
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-45">
                            <SelectValue placeholder="Select a time range" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7d">Last 7 Days</SelectItem>
                            <SelectItem value="30d">Last 30 Days</SelectItem>
                            <SelectItem value="3m">Last 3 Months</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent className="bg-none">
                <ChartContainer config={chartConfig}>
                    <LineChart
                        accessibilityLayer
                        data={chartData}
                        margin={{
                            top: 10,
                            right: 10,
                            left: 10,
                            bottom: 0,
                        }}
                    >
                        <ChartLegend content={<ChartLegendContent />} />
                        <CartesianGrid vertical={true} />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tickFormatter={value => value}
                        />
                        <YAxis tickLine={false} axisLine={false} tickMargin={8} width={30} />
                        <ChartTooltip
                            cursor={true}
                            content={
                                <ChartTooltipContent
                                    className="flex flex-col justify-center items-start gap-1"
                                    formatter={(value, name) => {
                                        if (timeRange === '7d') {
                                            return [
                                                value,
                                                name === 'xp' ? ' Current Week' : ' Previous Week',
                                            ];
                                        }
                                        return [value, 'XP'];
                                    }}
                                />
                            }
                        />
                        <Line
                            dataKey="xp"
                            type="monotoneX"
                            stroke={chartConfig.xp.color}
                            strokeWidth={2}
                            dot={{
                                fill: chartConfig.xp.color,
                                stroke: chartConfig.xp.color,
                                strokeWidth: 2,
                                r: 4,
                            }}
                            activeDot={{
                                r: 6,
                            }}
                        />
                        {timeRange === '7d' && (
                            <Line
                                dataKey="previousXp"
                                type="monotoneX"
                                stroke={chartConfig.previousXp.color}
                                strokeWidth={2}
                                strokeOpacity={0.4}
                                dot={{
                                    fill: chartConfig.previousXp.color,
                                    stroke: chartConfig.previousXp.color,
                                    strokeWidth: 2,
                                    r: 4,
                                }}
                                activeDot={{
                                    r: 6,
                                }}
                            />
                        )}
                    </LineChart>
                </ChartContainer>
            </CardContent>
            <CardFooter className="flex-col items-start gap-2 text-sm"></CardFooter>
        </Card>
    );
}
